import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import * as userActions from '../actions/user.action';
import {LoadUserSubscriptionSuccess} from '../actions/user.action';
import * as storeActions from '../actions';
import {AddActivity, AddActivityNotification} from '../actions';
import {DbResponse} from "../../shared/interceptors/db-response";
import {filter, map, mergeMap, switchMap} from 'rxjs/operators';
import * as activityNotificationActions from '../actions/activity-notifications.action';
import {ActivityNotificationAction} from "../../activity-notifications/activity-notification";
import {UserService} from "../../user/user.service";
import {CookieService} from "ngx-cookie";
import {UserSubscriptionCategory, UserSubscriptionIds} from "../../user/user-subscription";
import * as activityActions from "../actions/activities.action";
import {ActivityAction} from "../../activities/activity";
import {AuthService} from "../../auth/auth.service";
import {EMPTY, of} from 'rxjs';
import {WorkspaceService} from "../../workspace/workspace.service";
import * as userLocalStorageActions from "../actions/user-local-storage.action";
import * as workspaceActions from "../actions/workspace.action";
import {UserMapper} from "../../user/user-mapper";
import * as paymentActions from "../actions/payments.action";
import * as userPaymentMethodsActions from '../actions/user-payment-methods.action';
import * as companyInfoActions from '../actions/company-info.action';
import {GoogleAnalytics4Service} from "../../shared/services/google-analytics-4/google-analytics-4.service";

@Injectable()
export class UserEffects {

  initStore$ = createEffect(() => this._actions$.pipe(
    ofType(storeActions.INIT_STORE),
    switchMap(() => [
      new userActions.LoadUser(),
      new userActions.LoadUserSubscription(),
      new userLocalStorageActions.LoadUserLocalStorage(),
      new userActions.LoadUserAvailableSubscriptions(),
      new userActions.LoadPlanDescriptionMap(),
      new companyInfoActions.LoadCompanyInfo(),
    ])
  ));

  userLogIn$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.USER_LOG_IN),
    switchMap(() => [
      new userActions.LoadUser(),
      new userActions.LoadUserSubscription(),
      new userLocalStorageActions.LoadUserLocalStorage(),
      new userActions.LoadUserAvailableSubscriptions(),
      new userPaymentMethodsActions.LoadUserPaymentMethods()])
  ));

  paymentsSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.LOAD_USER_SUBSCRIPTION_SUCCESS),
    switchMap((action: LoadUserSubscriptionSuccess) => {
      const category: UserSubscriptionCategory = action.payload.subscription_category;
      if (![UserSubscriptionCategory.NONE, UserSubscriptionCategory.STARTER].includes(category)) {
        return [new paymentActions.LoadUserPayments()];
      } else {
        return EMPTY;
      }
    })
  ));
  updateUserSubscription$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.UPDATE_USER_SUBSCRIPTION),
    switchMap(() => [
      new userActions.LoadUser(),
      new userActions.LoadUserSubscription(),
      /*new userActions.LoadUserReferralSubscription(),*/
      new userActions.LoadUserAvailableSubscriptions(),
      new paymentActions.LoadUserPayments()
    ])
  ));

  loadUserAvailableSubscriptions$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.LOAD_USER_AVAILABLE_SUBSCRIPTIONS),
    switchMap(() => {
      return this._workspaceService.getUserAvailableSubscriptions()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new userActions.LoadUserAvailableSubscriptionsSuccess(response.data);
            }

            return new userActions.LoadUserAvailableSubscriptionsFail(response.message);
          })
        );
    })
  ));

  loadUserReferralSubscription$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.LOAD_USER_REFERRAL_SUBSCRIPTION),
    switchMap(() => {
      return this._workspaceService.getUserReferralSubscription()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new userActions.LoadUserReferralSubscriptionSuccess(response.data);
            }

            return new userActions.LoadUserReferralSubscriptionFail(response.message);
          })
        );
    })
  ));
  /*loadUserSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.LOAD_USER_SUCCESS),
    filter((action: LoadUserSuccess) => !!action.payload),
    switchMap(() => {
      return this._workspaceService.getUserReferralSubscription()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new userActions.LoadUserReferralSubscriptionSuccess(response.data);
            }

            return new userActions.LoadUserReferralSubscriptionFail(response.message);
          })
        );
    })
  ));*/

  loadPlanDescriptionMap$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.LOAD_PLAN_DESCRIPTION_MAP),
    switchMap(() => {
      return this._workspaceService.getPlanDescriptionMap()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new userActions.LoadPlanDescriptionMapSuccess(response.data);
            }

            return new userActions.LoadPlanDescriptionMapFail(response.message);
          })
        );
    })
  ));

  addActivityNotification$ = createEffect(() => this._actions$.pipe(
    ofType(activityNotificationActions.ADD_ACTIVITY_NOTIFICATION),
    filter((action: AddActivityNotification): boolean => action.payload.action === ActivityNotificationAction.UPDATE_USER_DATA),
    map((action: AddActivityNotification) => new userActions.UpdateUserData(action.payload.meta))
  ));
  clientLogout$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => (action.payload.action === ActivityAction.CLIENT_LOGOUT_EXCEPT_CURRENT_SESSION || action.payload.action === ActivityAction.CLIENT_LOGOUT)),
    mergeMap((action: AddActivity) => {
      const timeDiffAvailable = action?.payload?.date && ((new Date().getTime() - new Date(action?.payload?.date).getTime()) / 1000 * 60) < 2;
      if (!localStorage.getItem('2faSetupSuccess') && timeDiffAvailable) {
        this.authService.logout('', null, null);
      }
      localStorage.removeItem('2faSetupSuccess');
      if (action.payload.action === ActivityAction.CLIENT_LOGOUT_EXCEPT_CURRENT_SESSION) {
        return of(new userActions.LoadUser());
      }
      return of({ type: 'NO_ACTION' });
    })
  ));
  private callChat: boolean = true;
  loadUser$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.LOAD_USER),
    switchMap(() => {
      return this._userService.getUser()
        .pipe(
          filter((response: DbResponse) => !!response),
          mergeMap((response: DbResponse) => {
            if (response.success) {
              const workspaces = response.data && response.data.workspaces && UserMapper.workspaceMap(response.data.workspaces) || [];
              if (response.data && response.data.id) {
                if (typeof this._cookieService.get('db-ab-demo-testing') !== 'undefined') {
                  this._cookieService.remove('db-ab-demo-testing');
                }
                this._cookieService.put('db-user-id', response.data.id, {path: '/', domain: '10web.io'});
                if (this.callChat) {
                  this.callChat = false;
                }
                // Set GA4 client ID to user.id
                this._googleAnalytics4Service.setClientId(response.data.id);
              }

              return [
                new userActions.LoadUserSuccess(response.data),
                new workspaceActions.LoadWorkspaces(workspaces)
              ];
            }

            return [new userActions.LoadUserFail(response.message)];
          })
        );
    })
  ));
  private userSubscriptionIds: any = UserSubscriptionIds;
  loadUserSubscription$ = createEffect(() => this._actions$.pipe(
    ofType(userActions.LOAD_USER_SUBSCRIPTION),
    switchMap(() => {
      return this._workspaceService.getUserSubscription()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              if (response.data) {
                if (response.data.subscription_category !== 'registered') {
                  this._cookieService.put('db-user-subscr-id', response.data.subscription_id, {
                    path: '/',
                    domain: '10web.io'
                  });
                  this._cookieService.put('db-user-subscr-category', response.data.subscription_category, {
                    path: '/',
                    domain: '10web.io'
                  });
                  if (response.data.plan && response.data.plan.json_desc_short && response.data.plan.json_desc_short.hostingPlanCategory) {
                    this._cookieService.put('db-user-subscr-type', response.data.plan.json_desc_short.hostingPlanCategory, {
                      path: '/',
                      domain: '10web.io'
                    });
                  } else {
                    this._cookieService.remove('db-user-subscr-type', {path: '/', domain: '10web.io'});
                  }

                  if (response.data && response.data.subscription_category === UserSubscriptionCategory.STARTER) {
                    this._cookieService.put('db-trial-expire-date', response.data.hosting_trial_expire_date ? response.data.hosting_trial_expire_date : 'not-created', {
                      path: '/',
                      domain: '10web.io'
                    });
                  } else {
                    this._cookieService.remove('db-trial-expire-date', {path: '/', domain: '10web.io'});
                  }
                  this._cookieService.remove('idev', {path: '/', domain: '10web.io'});
                  this._cookieService.remove('_fprom_track', {path: '/', domain: '10web.io'});
                }
                return new userActions.LoadUserSubscriptionSuccess(response.data);
              }
            }
            return new userActions.LoadUserSubscriptionFail(response.message);
          })
        );
    })
  ));

  constructor(
    private _actions$: Actions,
    private _userService: UserService,
    private _workspaceService: WorkspaceService,
    private authService: AuthService,
    private _cookieService: CookieService,
    private _googleAnalytics4Service: GoogleAnalytics4Service,
  ) {
  }

}
